import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { ChevronRightIcon, Tick } from "../../../assets/icons/interface/interface";
import { sanitizeFileName } from "../../../handlers/documentStorage";
import { docSubTypes, dspDocTypes } from "../../../handlers/enums";
import { getErrorMessage } from "../../../handlers/errorHandling";
import { generateFileHash } from "../../../handlers/utilityFunctions";
import { useCreateSmartProfile } from "../../../services/create-resume/queries";
import FileUploadService from "../../../services/fileUploadService";
import { Button } from "../../JobsPage/components/Button";
import FileUpload from "./FileUpload";

const initialErrorsState: { file: string | null } = {
	file: null,
};

// Server-side limit from CreateDspDto.
const MAX_DESCRIPTION_LENGTH = 140;

// This modal only ever creates a resume, so the title is not worth asking for.
const RESUME_TITLE = "Resume";

type UploadInfo = { uploadUrl?: string; content_type?: string };
type CreateResponse = UploadInfo & { upload_info?: UploadInfo };

type CreateSmartProfilePayload = {
	dsp_title: string;
	doc_description: string;
	dsp_description: string;
	dsp_doc_type: string;
	doc_sub_type: string;
	file_name: string;
	file_hash: string;
};

type UploadResumeModalProps = {
	onClose: () => void;
};

function UploadResumeModal({ onClose }: UploadResumeModalProps) {
	const navigate = useNavigate();
	const [errors, setErrors] = useState(initialErrorsState);
	const [loading, setLoading] = useState(false);
	const [formData, setFormData] = useState<{ file: File | null }>({
		file: null,
	});

	const createSmartProfileMutation = useCreateSmartProfile({
		onSuccess: async (data: CreateResponse) => {
			// Create nests the presign under upload_info today, but the update endpoint
			// returns it bare. Accept either so this cannot break on a shape change.
			const uploadDetails = data?.upload_info ?? data ?? {};
			const file = formData.file;

			if (!uploadDetails.uploadUrl || !file) {
				setLoading(false);
				toast.error("The server did not return an upload link. Please try again.");
				return;
			}

			await FileUploadService.uploadResumeFileToBucketWithFetchAPI(
				{
					uploadUrl: uploadDetails.uploadUrl,
					file,
					contentType: uploadDetails.content_type,
				},
				() => {
					onClose();
					setLoading(false);
					toast.success("SmartProfile created successfully");
				},
				(error: unknown) => {
					setLoading(false);
					toast.error(
						getErrorMessage(error, "The file could not be uploaded. Please try again with a different file."),
					);
				},
			);
		},
		onError: (error: unknown) => {
			setLoading(false);
			toast.error(getErrorMessage(error, "Could not create your Smart Profile. Please try again."));
		},
	});

	// The query hooks live in untyped JS, so react-query infers a void payload.
	const createSmartProfile = createSmartProfileMutation.mutate as unknown as (
		payload: CreateSmartProfilePayload,
	) => void;

	function handleSubmit() {
		// Reset errors
		setErrors(initialErrorsState);
		let formHasErrors = false;

		//Form Validation
		if (formData.file === null) {
			formHasErrors = true;
			setErrors((prevState) => {
				return {
					...prevState,
					file: "Please upload a document file.",
				};
			});
		}

		if (formHasErrors) return;

		const file = formData.file;

		if (!file) return;

		//Submitting form data to the API
		setLoading(true);

		// The description field is hidden: the file name describes the document well enough,
		// and doc_description is capped at 140 characters server-side.
		const description = (file.name ?? "").slice(0, MAX_DESCRIPTION_LENGTH);

		generateFileHash(file)
			.then((fileHash: string) => {
				const payload = {
					dsp_title: RESUME_TITLE,
					// The API stores the description from doc_description; dsp_description
					// alone is accepted but silently discarded. Send both.
					doc_description: description,
					dsp_description: description,
					dsp_doc_type: dspDocTypes.RESUME,
					doc_sub_type: docSubTypes.RESUME.name,
					// Normalised so the stored storage_key matches the uploaded object.
					file_name: sanitizeFileName(file.name),
					file_hash: fileHash,
				};

				createSmartProfile(payload);
			})
			.catch((error: unknown) => {
				setLoading(false);
				toast.error(getErrorMessage(error, "Could not read that file. Please try again."));
			});
	}

	return (
		<div className="space-y-8 max-h-[calc(100vh-150px)] overflow-y-auto pr-2">
			<div className="flex flex-col md:flex-row leading-normal gap-6">
				<div className="w-full lg:w-1/2 space-y-8">
					<div className="text-md font-normal text-[#5C5C5C]">
						Create your Dproz Smart Profile (DSP) for instant visibility. Employers find you faster, and you unlock
						One-Click Apply for effortless job applications.
					</div>
					<div className="space-y-4">
						<FileUpload
							processDataOfUploadedFile={(file) => {
								setFormData((prev) => {
									return { ...prev, file: file as File };
								});
							}}
							removeFile={() => {
								setFormData((prev) => {
									return { ...prev, file: null };
								});
							}}
							fileName={formData?.file?.name || ""}
							fileError={errors.file}
							setFileError={(error) => setErrors({ ...errors, file: error })}
							multiple={false}
							supportedFiles={[{ name: "PDF", extension: ".pdf", mimeType: "application/pdf" }]}
							disableFileInput={false}
						/>
					</div>
					<div className="flex justify-end">
						<Button className="whitespace-nowrap" onClick={handleSubmit}>
							{loading ? <>Submitting...</> : "Submit"}
						</Button>
					</div>
				</div>
				<div className="w-full lg:w-1/2 bg-gray-50">
					<div className="bg-primary-100 rounded-xl px-6 py-7 flex flex-col md:flex-row items-center w-full justify-between gap-2">
						<div className="flex flex-col md:flex-row items-center gap-4">
							<img src="/images/resume-cta-1.png" alt="" />
							<div className="">
								<h3 className="text-[24px] font-bold text-gray-950">No resume? No problem. Create one in minutes.</h3>
								<p className="text-md font-normal text-gray-950">
									Craft a job-winning resume effortlessly with Dproz’s easy to-to-use tool.
								</p>
							</div>
						</div>
						<Button
							className="text-nowrap"
							onClick={() => {
								navigate("/resume/select-experience");
							}}
						>
							Start Here
						</Button>
					</div>
					<div className="p-[12px] mt-[48px] space-y-[8px] pb-12">
						<div>
							<div className="text-[20px] font-bold text-[#161616]">Want to Stand Out? Master Your Resume!</div>
							<div className="text-md font-normal text-gray-700">
								Want to improve your resume? Learn how to create a standout resume that catches employers&apos;
								attention!
							</div>
						</div>
						<div className="p-[16px] space-y-[8px]">
							<div className="text-md font-semibold text-[#3E424E]">Here are a few tips.</div>
							<div className="flex items-center gap-[8px] text-md font-normal text-[#3E424E]">
								<Tick className="w-[16px] h-[16px]" />
								Use a clean, professional layout with consistent formatting throughout the document.
							</div>
							<div className="flex items-center gap-[8px] text-md font-normal text-[#3E424E]">
								<Tick className="w-[16px] h-[16px]" />
								Use bullet points and short paragraphs to make information easy to scan.
							</div>
							<div className="flex items-center gap-[8px] text-md font-normal text-[#3E424E]">
								<Tick className="w-[16px] h-[16px]" />
								Focus on the most relevant and recent experiences that showcase your qualifications.
							</div>
						</div>
						<Button variant="ghost" className="text-primary-600">
							Learn More
							<ChevronRightIcon className="w-[16px] h-[16px] ml-[4px]" />
						</Button>
					</div>
				</div>
			</div>
		</div>
	);
}

export default UploadResumeModal;
